import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Select,
  Button,
  VStack,
  HStack,
  RadioGroup,
  Radio,
  FormErrorMessage,
  useToast,
  Text,
  Icon,
  Flex,
  Badge
} from '@chakra-ui/react';
import { AttachmentIcon } from '@chakra-ui/icons';
import { useAuth } from '../../contexts/AuthContext';
import projectService from '../../services/projectService';
import ticketService from '../../services/ticketService';

const TicketForm = ({ ticket, isEditing = false }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();

  // Form state
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    priority: 'medium',
    project: '',
    dueDate: ''
  });
  const [errors, setErrors] = useState({});
  const [projects, setProjects] = useState([]);
  const [files, setFiles] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [existingAttachments, setExistingAttachments] = useState([]);

  // Calculate due date based on priority
  const calculateDueDate = useCallback((priority) => {
    const today = new Date();
    let daysToAdd = 5; // default for medium
    
    if (priority === 'low') {
      daysToAdd = 10;
    } else if (priority === 'high') {
      daysToAdd = 3;
    }
    
    const dueDate = new Date(today);
    dueDate.setDate(today.getDate() + daysToAdd);
    return dueDate.toISOString().split('T')[0]; // format as YYYY-MM-DD
  }, []);

  // Load projects and ticket data if editing
  useEffect(() => {
    const loadData = async () => {
      try {
        // Fetch projects
        const response = await projectService.getProjects();
        const projectsList = response.data.data;
        setProjects(projectsList);

        // If editing, populate form with ticket data
        if (isEditing && ticket) {
          // Format dueDate for input if it exists
          const formattedDueDate = ticket.dueDate ?
            new Date(ticket.dueDate).toISOString().split('T')[0] : '';

          setFormData({
            title: ticket.title,
            description: ticket.description,
            priority: ticket.priority,
            project: ticket.project._id || ticket.project,
            dueDate: formattedDueDate
          });

          if (ticket.attachments) {
            setExistingAttachments(ticket.attachments);
          }
        } else if (projectsList.length > 0) {
          // Preselect first project and set initial due date based on default priority (medium)
          const initialDueDate = calculateDueDate('medium');
          setFormData(prev => ({
            ...prev,
            project: projectsList[0]._id,
            dueDate: initialDueDate
          }));
        }
      } catch (error) {
        toast({
          title: 'Error loading data',
          description: error.response?.data?.message || 'Something went wrong',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
    };

    loadData();
  }, [isEditing, ticket, toast, calculateDueDate]);

  // Handle input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => {
      const updatedData = {
        ...prev,
        [name]: value
      };
      
      // Update due date when priority changes (only for new tickets)
      if (name === 'priority' && !isEditing) {
        updatedData.dueDate = calculateDueDate(value);
      }
      
      return updatedData;
    });

    // Clear error when field is updated
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  // Handle file selection
  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files);
    setFiles(selectedFiles);
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};

    if (!formData.title.trim()) {
      newErrors.title = 'Title is required';
    }

    if (!formData.description.trim()) {
      newErrors.description = 'Description is required';
    }

    if (!formData.project) {
      newErrors.project = 'Please select a project';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      if (isEditing) {
        await ticketService.updateTicket(ticket._id, formData, files);
        toast({
          title: 'Ticket updated',
          description: 'Your ticket has been updated successfully',
          status: 'success',
          duration: 5000,
          isClosable: true,
        });
      } else {
        await ticketService.createTicket(formData, files);
        toast({
          title: 'Ticket created',
          description: 'Your ticket has been submitted successfully',
          status: 'success',
          duration: 5000,
          isClosable: true,
        });
      }

      // Redirect based on context (edit or create)
      if (isEditing) {
        navigate(`/tickets/${ticket._id}`);
      } else {
        navigate('/tickets');
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.message || 'Something went wrong',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box
      p={6}
      borderWidth="1px"
      borderRadius="lg"
      boxShadow="sm"
      bg="white"
    >
      <form onSubmit={handleSubmit}>
        <VStack spacing={4} align="stretch">
          <FormControl isRequired isInvalid={!!errors.project}>
            <FormLabel>Project</FormLabel>
            <Select
              name="project"
              value={formData.project}
              onChange={handleChange}
              placeholder="Select project"
            >
              {projects.map((project) => (
                <option key={project._id} value={project._id}>
                  {project.name}
                </option>
              ))}
            </Select>
            <FormErrorMessage>{errors.project}</FormErrorMessage>
          </FormControl>

          <FormControl isRequired isInvalid={!!errors.title}>
            <FormLabel>Title</FormLabel>
            <Input
              name="title"
              value={formData.title}
              onChange={handleChange}
              placeholder="Brief summary of the issue"
            />
            <FormErrorMessage>{errors.title}</FormErrorMessage>
          </FormControl>

          <FormControl isRequired isInvalid={!!errors.description}>
            <FormLabel>Description</FormLabel>
            <Textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              placeholder="Detailed description of the issue"
              minH="150px"
            />
            <FormErrorMessage>{errors.description}</FormErrorMessage>
          </FormControl>

          <FormControl>
            <FormLabel>Priority</FormLabel>
            <RadioGroup
              value={formData.priority}
              onChange={(value) => {
                const e = { target: { name: 'priority', value } };
                handleChange(e);
              }}
            >
              <HStack spacing={4}>
                <Radio value="low">Low</Radio>
                <Radio value="medium">Medium</Radio>
                <Radio value="high">High</Radio>
              </HStack>
            </RadioGroup>
          </FormControl>

          <FormControl>
            <FormLabel>Due Date</FormLabel>
            <Input
              type="date"
              name="dueDate"
              value={formData.dueDate}
              onChange={handleChange}
            />
            <Text fontSize="sm" color="gray.500" mt={1}>
              Set a deadline for this ticket
            </Text>
          </FormControl>

          <FormControl>
            <FormLabel>Attachments</FormLabel>
            <Input
              type="file"
              multiple
              onChange={handleFileChange}
              pt={1}
              sx={{
                '::file-selector-button': {
                  height: '36px',
                  padding: '0 16px',
                  borderRadius: 'md',
                  border: '1px solid',
                  borderColor: 'gray.200',
                  backgroundColor: 'gray.100',
                  marginRight: '16px',
                  color: 'gray.700',
                }
              }}
            />
            <Text fontSize="sm" color="gray.500" mt={1}>
              Max 5 files, 5MB each
            </Text>
          </FormControl>

          {/* Show existing attachments when editing */}
          {isEditing && existingAttachments.length > 0 && (
            <Box>
              <FormLabel>Existing Attachments</FormLabel>
              <Flex wrap="wrap" gap={2}>
                {existingAttachments.map((file, index) => (
                  <Badge
                    key={index}
                    p={2}
                    borderRadius="md"
                    variant="outline"
                    colorScheme="brand"
                  >
                    <HStack>
                      <Icon as={AttachmentIcon} />
                      <Text fontSize="sm">{file.originalname}</Text>
                    </HStack>
                  </Badge>
                ))}
              </Flex>
            </Box>
          )}

          {/* New files preview */}
          {files.length > 0 && (
            <Box>
              <FormLabel>Selected Files</FormLabel>
              <Flex wrap="wrap" gap={2}>
                {files.map((file, index) => (
                  <Badge
                    key={index}
                    p={2}
                    borderRadius="md"
                    variant="outline"
                    colorScheme="blue"
                  >
                    <HStack>
                      <Icon as={AttachmentIcon} />
                      <Text fontSize="sm">{file.name}</Text>
                    </HStack>
                  </Badge>
                ))}
              </Flex>
            </Box>
          )}

          <Button
            type="submit"
            colorScheme="brand"
            size="lg"
            isLoading={isSubmitting}
            mt={4}
          >
            {isEditing ? 'Update Ticket' : 'Submit Ticket'}
          </Button>
        </VStack>
      </form>
    </Box>
  );
};

export default TicketForm;
